/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/
/* tslint:disable: no-duplicate-imports */
import classname from 'classnames';
import { Module, Node } from '@msdyn365-commerce-modules/utilities';
import MsDyn365 from '@msdyn365-commerce/core';
import * as React from 'react';
import { useInView } from 'react-intersection-observer';
import { getPromoAnalyticsConfig } from '../../themes/mattressfirm/views/tealiumAnalytics/analytics';
import { htmltoText } from '../../Utilities/analytics/htmlToText';
import { firePromotionClick, firePromotionImpression } from '../../Utilities/analytics/promotionClickTrack';
import { getDynamicImageSettings, getResponsiveImage } from '../../Utilities/get-dynamic-image';
import _getViewPort from '../../Utilities/get-view-port';
import LinksComponent from './components/links';
import { IContentBlockViewProps } from './mfrm-category-banner';

// tslint:disable-next-line: max-func-body-length
const ContentBlockView: React.FC<IContentBlockViewProps> = props => {
    const {
        contentBlockContainer,
        imageContainer,
        detailsContainer,
        title,
        text,
        links,
        image,
        contentBlockAnchorTag,
        imageLink,
        fullyClickableBanner,
        useDynamicImages,
        dynamicImages,
        imageAriaLabel,
        logo
    } = props;

    const { tealiumEvent, enhancedEcommTracking, tealiumPromotion, heading, paragraph, textAlignment } = props.config;

    const isPromotion = enhancedEcommTracking === 'promotion';
    const pagePath = MsDyn365.isBrowser
        ? window.location.href.split(location.host)[1].includes('?')
            ? window.location.href.split(location.host)[1].split('?')[0]
            : window.location.href.split(location.host)[1]
        : '';
    const paragraphText = paragraph ? htmltoText(paragraph.toString()).trim() : '';
    let promoAnalyticsConfig = getPromoAnalyticsConfig(
        props.config.image,
        imageLink || (links && links[0]?.linkUrl?.destinationUrl),
        pagePath,
        paragraphText,
        tealiumPromotion,
        true,
        heading?.text
    );

    const [ref, inView] = useInView({
        triggerOnce: true,
        rootMargin: '25px'
    });

    const handleAnalytics = (isImageClick?: boolean, ctaUrl?: string) => {
        const pageURL = MsDyn365.isBrowser && window.location.pathname;
        if (MsDyn365.isBrowser) {
            // @ts-ignore
            if (utag) {
                // @ts-ignore
                utag.link({
                    event: tealiumEvent?.event,
                    event_category: tealiumEvent?.event_category,
                    event_action: tealiumEvent?.event_action,
                    event_label: tealiumEvent?.event_label ? `${tealiumEvent?.event_label} ${pageURL}` : ``,
                    event_noninteraction: tealiumEvent?.event_noninteraction === true ? 'true' : 'false'
                });
            }
            if (isPromotion) {
                if (!isImageClick) {
                    promoAnalyticsConfig = getPromoAnalyticsConfig(
                        props.config.image,
                        ctaUrl,
                        pagePath,
                        paragraphText,
                        tealiumPromotion,
                        true,
                        heading?.text
                    );
                }
                promoAnalyticsConfig && firePromotionClick({ ...promoAnalyticsConfig });
            }

            if (imageLink) {
                window.location.href = imageLink;
            } else if (!isImageClick && ctaUrl) {
                window.location.href = ctaUrl;
            }
        }
    };

    React.useEffect(() => {
        if (MsDyn365.isBrowser && isPromotion && inView) {
            promoAnalyticsConfig && firePromotionImpression({ ...promoAnalyticsConfig });
        }
    }, [inView]);

    let currentImage = image;

    // Only use Responsive Image if Dynamic Images are defined
    if (dynamicImages && useDynamicImages) {
        currentImage = getResponsiveImage({
            desktop: dynamicImages?.desktopImage,
            tablet: dynamicImages?.tabletImage,
            mobile: dynamicImages?.mobileImage,
            defaultImg: props.config.image,
            imageSettingViewports: getDynamicImageSettings({ moduleName: 'mfrm-category-banner' })
        });
    }

    if (useDynamicImages) {
        return <div className='category-banner__dynamic-image'> {currentImage} </div>;
    }

    const imageLinkContent = (
        <Module {...contentBlockContainer} data-cy='category-banner'>
            <Node
                {...contentBlockAnchorTag}
                href={imageLink}
                ref={ref}
                className={contentBlockAnchorTag ? contentBlockAnchorTag.className : ''}
                aria-label={imageAriaLabel}
            >
                <Node
                    {...imageContainer}
                    // tslint:disable-next-line: jsx-no-lambda
                    onClick={(event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
                        event.preventDefault();
                        handleAnalytics(true, undefined);
                    }}
                >
                    {image}
                </Node>
            </Node>
            <Node {...detailsContainer}>
                {title}
                {text}
                {links}
            </Node>
        </Module>
    );

    const buttonLinkContent = (
        <Module {...contentBlockContainer}>
            <Node {...imageContainer} ref={ref}>
                {image}
            </Node>
            <Node {...detailsContainer}>
                {title}
                {text}
                {props.links && props.links.length > 0 && props.handleTextChange && props.telemetryContent && (
                    textAlignment === 'left' ?
                    (
                        <div className='ms-content-block__cta-left'>
                            <button className={classname(`mfrm-category-banner__cta-button ${!logo ? 'mfrm-category-banner__cta-button__sleep-disruptor':''}`)}
                                    onClick={(event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
                                        event.preventDefault();
                                        event.stopPropagation();
                                        handleAnalytics(false, links && links[0].linkUrl.destinationUrl);
                                    }}
                            >
                                <div className='mfrm-category-banner__cta-button__text'>
                                    {links && links[0].linkText}
                                </div>
                                <div>{logo ?? logo}</div>
                            </button>
                        </div>
                    ):
                    (<LinksComponent
                        {...{
                            links: props.links,
                            onTextChange: props.handleTextChange,
                            requestContext: props.context.request,
                            telemetryContent: props.telemetryContent,
                            onClickOverride: (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>, ctaUrl: string) => {
                                event.preventDefault();
                                event.stopPropagation();
                                handleAnalytics(false, ctaUrl);
                            }
                        }}
                    />)
                )}
            </Node>
        </Module>
    );

    if (imageLink) {
        return imageLinkContent;
    } else if (fullyClickableBanner){
        return (
            <div className='mfrm-category-banner__clickable-banner'
                role='link'
                tabIndex={0}
                onClick={(event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
                    event.preventDefault();
                    handleAnalytics(false, links && links[0].linkUrl.destinationUrl);
                }}
                data-cy='category-banner'
            >
                <a href={links && links[0].linkUrl.destinationUrl}
                   className='msc-carousel__anchor'
                >
                    {buttonLinkContent}
                </a>
            </div>
        );
    }else{
        return buttonLinkContent;
    }
};

export default ContentBlockView;
